import { useMemo } from 'react'
import Stack from '@mui/material/Stack'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { PieChart, PieValueType } from '@mui/x-charts-pro'
import map from 'lodash/map'

import { AbstractsStatistics } from '../../../../types'
import { Dot } from './Dot'

interface Props {
  statistics: AbstractsStatistics | null | undefined
}

export default function AdminAbstractsDetails({ statistics }: Props) {
  const { t } = useTranslation('admin')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const isTinyMobile = useMediaQuery(theme.breakpoints.down('md'))

  const dataDomains: PieValueType[] = useMemo(() => {
    const arr = map(statistics?.domain_stats ?? [], ({ domain, count }) => {
      return {
        id: domain.id,
        value: count,
        label: `${count}: ` + domain.name,
        // color: '',
      } as PieValueType
    })
    return arr as unknown as PieValueType[]
  }, [statistics?.domain_stats])

  const dataThemes: PieValueType[] = useMemo(() => {
    const arr = map(statistics?.theme_stats ?? [], ({ theme, count }) => {
      return {
        id: theme.id,
        value: count,
        label: `${count}: ` + (isTinyMobile ? theme.name.slice(0, 30) + '...' : theme.name),
        // color: '',
      } as PieValueType
    })
    return arr as unknown as PieValueType[]
  }, [statistics?.theme_stats, isTinyMobile])

  return (
    <Stack direction="column">
      <Stack direction={{ md: 'column', lg: 'row' }}>
        <Stack direction="column" flex="1 0">
          <Stack direction={{ md: 'row', lg: 'column' }}>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Nombre résumés soumis')} :</h6>
              <Dot>{statistics?.submitted_abstracts ?? 'NC'}</Dot>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Nombre évaluation')} :</h6>
              <Dot backgroundColor={theme => theme.palette.primary.main}>
                {statistics?.evaluations ?? 'NC'}
              </Dot>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Nombre évaluateur')} :</h6>
              <Dot>{statistics?.evaluators ?? 'NC'}</Dot>
            </Stack>
            <Stack direction="row" alignItems="center" gap="0.2rem" flex="1 0">
              <h6>{t('Nombre actif évaluateur')} :</h6>
              <Dot backgroundColor={theme => theme.palette.primary.main}>
                {statistics?.active_evaluators ?? 'NC'}
              </Dot>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" flex="1 0">
          <PieChart
            series={[
              {
                data: dataDomains,
              },
            ]}
            slotProps={{
              legend: {
                direction: 'column',
                position: {
                  vertical: 'top',
                  horizontal: isMobile ? 'left' : 'right',
                },
                itemMarkWidth: 2,
                itemMarkHeight: 20,
                markGap: 5,
                itemGap: 5,
              },
            }}
            margin={
              isMobile
                ? { top: 0, right: 0, bottom: 0, left: 200 }
                : { top: 0, right: 200, bottom: 0, left: 0 }
            }
            height={170}
          />
        </Stack>
        <Stack direction="row" flex="2 0">
          <PieChart
            series={[
              {
                data: dataThemes,
              },
            ]}
            slotProps={{
              legend: {
                direction: 'column',
                labelStyle: {
                  fontSize: 12,
                },
                position: {
                  vertical: 'top',
                  horizontal: isMobile ? 'left' : 'right',
                },
                itemMarkWidth: 2,
                itemMarkHeight: 12,
                markGap: 5,
                itemGap: 5,
              },
            }}
            margin={
              isMobile
                ? { top: 0, right: 0, bottom: 0, left: 200 }
                : { top: 0, right: 400, bottom: 0, left: 0 }
            }
            height={170}
          />
        </Stack>
      </Stack>
    </Stack>
  )
}
